// THE OPERATIONS INBOX'S ACTION-ITEM CARDS, AS PURE READINGS. Two kinds, one
// after the other in an item's life:
//
//   REVIEW ACTION ITEMS · <brand>: one card per brand while any of its items
//   waits to be confirmed. Its units are exactly the Action Items page's "to
//   confirm" filter over All (`needsConfirm`, not archived): an item whose
//   status nobody has set. An item is reviewed when a person sets its status,
//   which the store already keeps, with its History row. The card counts down
//   ("57 to review") and is done when none is left.
//
//   COMPLETE AN ACTION ITEM: one card per item we owe, made a set number of
//   days (the kind's setting, 1) after the day its status was confirmed, while
//   it is open work with the ball on our side. Done when the item is: Done,
//   Dropped, archived, or closed as no longer valid (gone quiet, deleted, made
//   a standing rule, sent back to be confirmed, or the ball passed to the
//   client, which reopens it when the ball comes back). An item never
//   reviewed never gets one, and nor does one closed during its review or one
//   the client owes.
//
// ⚠️ WHEN AN ITEM WAS CONFIRMED IS ITS HISTORY'S: the latest status row that
// set a status where there was none (`confirmedOf`). An item with a status and
// no such row (none on the 2026-11-02 snapshot) is dated by when it was raised.
//
// No database: lib/server/ops-inbox-items.ts reads the items and their History
// rows, and the tick hands each card's reading to `stepOwed`
// (lib/ops-inbox-cards.ts). Shared, not server-only, and erasable TypeScript:
// the tick runs in a sidecar under node's type stripping.

import { isClosedStatus, STATUS_WORDS, type ActionItemStatus } from './action-items';
import { isInactive, isStanding, needsConfirm, OWES_TAG, type ItemRecord } from './client-slack-action-items';
import type { CardDetail, CardRow, OwedReading } from './ops-inbox-cards';
import { addDaysUtc, formatShort } from './schedule-dates';
import { operationsDay } from './throughput-operations';

const plural = (n: number, word: string): string => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

/** A list of strings back from `detail_json`, the non-strings dropped. */
const stringsOf = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const textOf = (d: CardDetail | null | undefined, k: string): string | null => {
  const v = d?.[k];
  return typeof v === 'string' && v !== '' ? v : null;
};

/** An action item as both cards read it: the Action Items page's record. */
export type CardItem = Pick<ItemRecord, 'id' | 'title' | 'createdAt' | 'due' | 'assigneeEmail' | 'archived' | 'status' | 'suggestedStatus' | 'owes' | 'type' | 'inactive'>;

/**
 * ONE HISTORY ROW OF AN ITEM THAT BEARS ON ITS CARDS: its status set or
 * cleared, archived or back, gone quiet or re-opened, its type changed, its
 * ball moved. `from` and `to` are the field's values (a status, true / false,
 * a type, `us` / `them`), null for none. `actor` null: the model's (the
 * ledger moves the ball too). `made`: the row that opened the item.
 */
export interface ItemMark {
  field: 'status' | 'archived' | 'inactive' | 'type' | 'owes';
  at: number;
  actor: string | null;
  made: boolean;
  from: string | boolean | null;
  to: string | boolean | null;
}

/** Waiting to be reviewed: the Action Items page's "to confirm" over All (not archived, no status set, not inactive). */
export const toReview = (it: CardItem): boolean => !it.archived && needsConfirm(it);

/**
 * WHEN AND BY WHOM AN ITEM'S STATUS WAS CONFIRMED: the latest History row that
 * set a status where there was none (a person's Confirm or Save, or the
 * status an item was made with by hand). Null while no status is set. An item
 * with a status and no such row is dated by when it was raised, by no one.
 */
export function confirmedOf(it: CardItem, marks: readonly ItemMark[]): { at: number; actor: string | null; made: boolean } | null {
  if (it.status === null) return null;
  let best: ItemMark | null = null;
  for (const m of marks) {
    if (m.field === 'status' && m.from === null && m.to !== null && (best === null || m.at >= best.at)) best = m;
  }
  return best === null ? { at: it.createdAt, actor: null, made: true } : { at: best.at, actor: best.actor, made: best.made };
}

/** The latest of an item's rows that `pick` keeps, or null. */
function latest(marks: readonly ItemMark[], pick: (m: ItemMark) => boolean): ItemMark | null {
  let best: ItemMark | null = null;
  for (const m of marks) if (pick(m) && (best === null || m.at >= best.at)) best = m;
  return best;
}

// ─── Review action items ────────────────────────────────────────────────────

/** Why an item left a review card's list: its status confirmed, archived, gone quiet, or no longer there. */
export type ReviewGone = 'confirmed' | 'archived' | 'inactive' | 'gone';
const REVIEW_GONE: readonly ReviewGone[] = ['confirmed', 'archived', 'inactive', 'gone'];
export const reviewGoneOf = (v: unknown): ReviewGone => ((REVIEW_GONE as readonly unknown[]).includes(v) ? (v as ReviewGone) : 'gone');

/** Why one item is off the review list now, with who and when as its History says. Null: it still waits. */
function reviewLeft(it: CardItem | undefined, marks: readonly ItemMark[]): { why: ReviewGone; actor: string | null; at: number | null } | null {
  if (it === undefined) return { why: 'gone', actor: null, at: null };
  if (toReview(it)) return null;
  if (it.archived) {
    const m = latest(marks, (x) => x.field === 'archived' && x.to === true);
    return { why: 'archived', actor: m?.actor ?? null, at: m?.at ?? null };
  }
  if (it.status !== null) {
    const c = confirmedOf(it, marks);
    return { why: 'confirmed', actor: c?.actor ?? null, at: c?.made ? null : (c?.at ?? null) };
  }
  const m = latest(marks, (x) => x.field === 'inactive' && x.to === true);
  return { why: 'inactive', actor: null, at: m?.at ?? null };
}

/** Why a review card closed for good, in words: "Dana confirmed the last item". `who` null: nobody the History names. */
export function reviewClosedWords(why: ReviewGone, who: string | null): string {
  switch (why) {
    case 'confirmed': return who ? `${who} confirmed the last item` : 'the last item was confirmed';
    case 'archived': return who ? `${who} archived the last item` : 'the last item was archived';
    case 'inactive': return 'the last item went quiet';
    case 'gone': return 'no item is left to confirm';
  }
}

/**
 * A REVIEW ACTION ITEMS CARD'S READING (`stepOwed`'s), for one brand. Owed:
 * its items waiting to be confirmed (`toReview`), oldest first. Due on the
 * day the oldest was raised, so it goes late; back from the archive when an
 * item raised after the archive waits. Its counter is the day's work: the
 * items confirmed today (a person's Confirm, not an item made with a status)
 * of those plus the ones still waiting, drawn as what is left ("57 to
 * review"). Done when none waits, explained by what took the last of the
 * card's items off the list ("Dana confirmed the last item").
 *
 *   items              the waiting items' ids
 *   closed / closedOn  why it closed for good (`ReviewGone`), and the day
 */
export function reviewItemsReading(card: CardRow | null, items: readonly CardItem[], marks: ReadonlyMap<string, readonly ItemMark[]>, today: string, at: number, nameOf: (email: string) => string): OwedReading {
  const waiting = items.filter(toReview).sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
  const marksOf = (id: string): readonly ItemMark[] => marks.get(id) ?? [];
  const confirmedToday = items.filter((it) => {
    const c = confirmedOf(it, marksOf(it.id));
    return c !== null && !c.made && !it.archived && operationsDay(c.at) === today;
  }).length;
  const owed = waiting.length;
  const oldest = waiting[0];
  const dueOn = oldest === undefined ? (card?.dueOn ?? today) : operationsDay(oldest.createdAt);
  const newest = waiting.reduce<number | null>((m, it) => (m === null || it.createdAt > m ? it.createdAt : m), null);
  if (owed === 0 && card !== null) {
    // A card already closed keeps its why and its counter: a later pass must not rewrite them.
    const kept = card.state === 'done' && typeof card.detail?.closed === 'string' ? card.detail : null;
    const byId = new Map(items.map((it) => [it.id, it]));
    const left = stringsOf(card.detail?.items).flatMap((id) => {
      const l = reviewLeft(byId.get(id), marksOf(id));
      return l === null ? [] : [l];
    });
    // The newest leaving with a time; else the first one's why, with no one named.
    const last = left.filter((l) => l.at !== null).sort((a, b) => (b.at ?? 0) - (a.at ?? 0))[0] ?? left[0] ?? null;
    const why = kept !== null ? reviewGoneOf(kept.closed) : (last?.why ?? 'gone');
    const who = last?.actor ? nameOf(last.actor) : null;
    const closedOn = kept !== null && typeof kept.closedOn === 'string' ? kept.closedOn : operationsDay(last?.at ?? at);
    return {
      owed: 0,
      dueOn,
      target: kept !== null ? card.target : confirmedToday,
      doneN: kept !== null ? card.doneN : confirmedToday,
      done: kept === null && last !== null && last.at !== null ? { actor: last.actor, at: last.at, via: 'app' } : null,
      workSince: null,
      extra: { items: [], closed: why, closedOn },
      notes: { closed: `closed: nothing left to review, ${reviewClosedWords(why, who)}` },
    };
  }
  return {
    owed,
    dueOn,
    target: owed + confirmedToday,
    doneN: confirmedToday,
    done: null,
    workSince: newest,
    extra: { items: waiting.map((it) => it.id) },
    notes: {
      made: oldest === undefined ? undefined : `opened: ${plural(owed, 'item')} to review, the oldest raised ${formatShort(dueOn)}`,
      reopened: `reopened: ${plural(owed, 'item')} to review`,
      back: `${plural(owed, 'item')} to review`,
    },
  };
}

// ─── Complete an action item ────────────────────────────────────────────────

/**
 * Why a complete card closed: the item marked Done or Dropped, archived, gone
 * quiet (the re-check's inactive), made a standing rule, its status cleared so
 * it waits to be confirmed again, the ball passed to the client (`theirs`), or
 * no longer there.
 */
export type CompleteClosed = 'done' | 'dropped' | 'archived' | 'inactive' | 'standing' | 'unconfirmed' | 'theirs' | 'gone';
const COMPLETE_CLOSED: readonly CompleteClosed[] = ['done', 'dropped', 'archived', 'inactive', 'standing', 'unconfirmed', 'theirs', 'gone'];
export const completeClosedOf = (v: unknown): CompleteClosed => ((COMPLETE_CLOSED as readonly unknown[]).includes(v) ? (v as CompleteClosed) : 'gone');

/**
 * Open work a person confirmed that we owe: a status set and not Done or
 * Dropped, not archived, not gone quiet, not a standing rule, the ball ours.
 * An item the client owes is chased through its brand's list, not a card.
 */
export function toComplete(it: CardItem | undefined): it is CardItem {
  return it !== undefined && it.status !== null && !isClosedStatus(it.status) && !it.archived && !isInactive(it) && !isStanding(it) && it.owes === 'us';
}

/** Why an item no longer needs completing, with who and when as its History says. */
function completeLeft(it: CardItem | undefined, marks: readonly ItemMark[]): { why: CompleteClosed; actor: string | null; at: number | null } {
  if (it === undefined) return { why: 'gone', actor: null, at: null };
  const row = (why: CompleteClosed, m: ItemMark | null) => ({ why, actor: m?.actor ?? null, at: m?.at ?? null });
  if (it.archived) return row('archived', latest(marks, (x) => x.field === 'archived' && x.to === true));
  if (it.status === 'done' || it.status === 'dropped') return row(it.status, latest(marks, (x) => x.field === 'status' && x.to === it.status));
  if (it.status === null) return row('unconfirmed', latest(marks, (x) => x.field === 'status' && x.to === null));
  if (isInactive(it)) return { why: 'inactive', actor: null, at: latest(marks, (x) => x.field === 'inactive' && x.to === true)?.at ?? null };
  if (isStanding(it)) return row('standing', latest(marks, (x) => x.field === 'type' && x.to === 'standing_instruction'));
  return row(it.owes === 'them' ? 'theirs' : 'gone', latest(marks, (x) => x.field === 'owes' && x.to === 'them'));
}

/**
 * WHY A COMPLETE CARD IS DONE, in words: "Dana marked it Done". `who` null:
 * nobody the History names.
 */
export function completeClosedWords(why: CompleteClosed, who: string | null): string {
  const by = who ? ` by ${who}` : '';
  switch (why) {
    case 'done': return who ? `${who} marked it Done` : 'Marked Done';
    case 'dropped': return who ? `${who} dropped it` : 'Dropped';
    case 'archived': return `Archived${by}`;
    case 'inactive': return 'Closed: the item went quiet';
    case 'standing': return `Made a standing rule${by}`;
    case 'unconfirmed': return who ? `Back to review: ${who} cleared its status` : 'Back to review: its status was cleared';
    case 'theirs': return who ? `${who} passed the ball to the client` : 'The ball passed to the client';
    case 'gone': return 'Closed: the item is gone';
  }
}

/** The same why, as the card's history line says it: "closed: Dana marked it Done", "closed: the item went quiet". */
export function completeClosedNote(why: CompleteClosed, who: string | null): string {
  const by = who ? ` by ${who}` : '';
  const said = ((): string => {
    switch (why) {
      case 'done': return who ? `${who} marked it Done` : 'marked Done';
      case 'dropped': return who ? `${who} dropped it` : 'dropped';
      case 'archived': return `archived${by}`;
      case 'inactive': return 'the item went quiet';
      case 'standing': return `made a standing rule${by}`;
      case 'unconfirmed': return who ? `back to review, ${who} cleared its status` : 'back to review, its status was cleared';
      case 'theirs': return who ? `${who} passed the ball to the client` : 'the ball passed to the client';
      case 'gone': return 'the item is gone';
    }
  })();
  return `closed: ${said}`;
}

/** The item's own facts a complete card keeps, so the inbox reads one row: never its words but the title. */
function itemFacts(it: CardItem, confirmed: { at: number; actor: string | null }): CardDetail {
  return {
    item: it.id,
    title: it.title,
    due: it.due,
    assignee: it.assigneeEmail,
    owes: it.owes,
    status: it.status,
    confirmedOn: operationsDay(confirmed.at),
    confirmedBy: confirmed.actor,
  };
}

/**
 * A COMPLETE CARD'S READING (`stepOwed`'s), for one item. Owed from
 * `firstAfterDays` after the day its status was confirmed (the kind's setting,
 * 1: the next day) while it is open work we owe (`toComplete`); once made,
 * while it stays so. Due on that day, or on the day the ball came to us when
 * that is later (an item confirmed while the client owed it, or a closed card
 * whose ball came back), or on a later one a snooze moved it to; so it goes
 * late, and an archived card comes back when the item is confirmed again
 * after the archive. Done when the item is no longer open work we owe, saying
 * why and who ("Dana marked it Done", "Archived by Dana", "Dana passed the
 * ball to the client").
 *
 *   item / title / due / assignee / owes / status   the item, as last read
 *   confirmedOn / confirmedBy                        when and by whom it was confirmed
 *   closed / closedOn                                why it closed for good, and the day
 */
export function completeReading(
  card: CardRow | null,
  it: CardItem | undefined,
  marks: readonly ItemMark[],
  firstAfterDays: number,
  today: string,
  at: number,
  nameOf: (email: string) => string,
): OwedReading {
  const open = card !== null && (card.state === 'todo' || card.state === 'snoozed');
  const confirmed = toComplete(it) ? confirmedOf(it, marks) : null;
  if (toComplete(it) && confirmed !== null) {
    // The ball's latest move to us, by a person or the ledger (the row that opened the item is never after its confirming).
    const ours = latest(marks, (x) => x.field === 'owes' && x.to === 'us');
    const oursDay = ours === null ? null : operationsDay(ours.at);
    const afterReview = addDaysUtc(operationsDay(confirmed.at), Math.max(0, firstAfterDays));
    const startDay = oursDay !== null && oursDay > afterReview ? oursDay : afterReview;
    if (today >= startDay || open) {
      const who = confirmed.actor ? nameOf(confirmed.actor) : null;
      const said = `${who ? `${who} confirmed it` : 'confirmed'} as ${STATUS_WORDS[it.status as ActionItemStatus]} on ${formatShort(operationsDay(confirmed.at))}`;
      const mover = ours?.actor ? nameOf(ours.actor) : null;
      const backToUs = `we owe it again${mover ? `, ${mover} passed the ball back` : ''}`;
      const cameToUs = oursDay !== null && oursDay > operationsDay(confirmed.at) ? `the ball came to us on ${formatShort(oursDay)}; ${said}` : said;
      return {
        owed: 1,
        // A snooze moves an open card's day on; it never comes back earlier than the day after confirming.
        dueOn: open && card.dueOn > startDay ? card.dueOn : startDay,
        target: null,
        doneN: null,
        done: null,
        workSince: confirmed.at,
        extra: itemFacts(it, confirmed),
        notes: { made: `opened: ${cameToUs}`, reopened: `reopened: ${card?.detail?.closed === 'theirs' ? backToUs : said}`, back: said },
      };
    }
  }
  if (card === null) return { owed: 0, dueOn: today, target: null, doneN: null, done: null, workSince: null, extra: {}, notes: {} };
  // Closing, or closed: a card already closed keeps its why. (A done or archived card whose item is open work again
  // before its day comes here too: closed, it keeps its why; archived, `stepOwed` leaves it be while nothing is owed.)
  const kept = card.state === 'done' && typeof card.detail?.closed === 'string' ? card.detail : null;
  const left = completeLeft(it, marks);
  const why = kept !== null ? completeClosedOf(kept.closed) : left.why;
  const closedOn = kept !== null && typeof kept.closedOn === 'string' ? kept.closedOn : operationsDay(left.at ?? at);
  const facts: CardDetail = { ...(card.detail ?? {}) };
  delete facts.owed;
  if (it !== undefined) Object.assign(facts, { title: it.title, due: it.due, assignee: it.assigneeEmail, owes: it.owes, status: it.status });
  return {
    owed: 0,
    dueOn: card.dueOn,
    target: null,
    doneN: null,
    done: kept === null && left.at !== null ? { actor: left.actor, at: left.at, via: 'app' } : null,
    workSince: null,
    extra: { ...facts, closed: why, closedOn },
    notes: { closed: completeClosedNote(why, left.actor ? nameOf(left.actor) : null) },
  };
}

/** A complete card's item, back from its `detail_json`, each value's type checked. Null: no item named. */
export function cardItemOf(detail: CardDetail | null): { id: string; title: string; due: string | null; assignee: string | null; owesTag: string | null } | null {
  const id = textOf(detail, 'item');
  if (id === null) return null;
  const owes = detail?.owes;
  return {
    id,
    title: textOf(detail, 'title') ?? '',
    due: textOf(detail, 'due'),
    assignee: textOf(detail, 'assignee'),
    owesTag: owes === 'us' || owes === 'them' ? OWES_TAG[owes] : null,
  };
}
